export default function PrivacyPolicy() {
  const sections = [
    {
      title: "Information We Collect",
      content: [
        {
          subtitle: "Teacher Information",
          items: [
            "Name and email address for account creation",
            "School or institution name (optional)",
            "Payment information for subscription billing",
            "Communication preferences and settings"
          ]
        },
        {
          subtitle: "Student Information", 
          items: [
            "First name and last initial only",
            "Unique student ID (generated by system)",
            "Exam responses and performance data",
            "Login timestamps for security monitoring"
          ]
        },
        {
          subtitle: "Technical Information",
          items: [
            "Browser type and version for compatibility",
            "IP address for security and fraud prevention",
            "Device information for exam security",
            "Usage analytics to improve our service"
          ]
        }
      ]
    },
    {
      title: "How We Use Information", 
      content: [
        {
          subtitle: "Educational Services",
          items: [
            "Deliver online exams and assessments",
            "Generate performance reports and analytics",
            "Provide real-time monitoring and cheating detection",
            "Enable communication between teachers and students"
          ]
        },
        {
          subtitle: "Service Improvement",
          items: [
            "Analyze usage patterns to enhance features",
            "Conduct research to improve AI algorithms",
            "Optimize system performance and reliability",
            "Develop new educational tools and resources"
          ]
        },
        {
          subtitle: "Legal and Security",
          items: [
            "Comply with legal obligations and regulations",
            "Protect against fraud, abuse, and security threats",
            "Respond to legal requests and court orders",
            "Enforce our Terms of Service and policies"
          ]
        }
      ]
    },
    {
      title: "Information Sharing",
      content: [
        {
          subtitle: "We DO Share Information With:",
          items: [
            "Teachers - access to their students' exam data only",
            "Service providers - only data necessary for operations",
            "Legal authorities - when required by law",
            "Emergency contacts - in case of safety concerns"
          ]
        },
        {
          subtitle: "We DO NOT Share Information With:",
          items: [
            "Advertising companies or marketers",
            "Data brokers or analytics companies",
            "Social media platforms",
            "Third parties for commercial purposes",
            "Other educational institutions without consent"
          ]
        }
      ]
    },
    {
      title: "Data Security",
      content: [
        {
          subtitle: "Encryption and Protection",
          items: [
            "AES-256 encryption for all data at rest",
            "TLS 1.3 encryption for data in transit",
            "Regular security audits and penetration testing",
            "Multi-factor authentication options"
          ]
        },
        {
          subtitle: "Access Controls",
          items: [
            "Role-based access permissions",
            "Regular employee security training",
            "Background checks for all staff",
            "Audit logs for all data access"
          ]
        }
      ]
    },
    {
      title: "Student Privacy Rights",
      content: [
        {
          subtitle: "FERPA Compliance",
          items: [
            "Educational records are protected under FERPA",
            "Parents have rights to access student records",
            "Students over 18 control their own records",
            "Written consent required for most disclosures"
          ]
        },
        {
          subtitle: "COPPA Compliance",
          items: [
            "No personal information collected from children under 13",
            "Parental consent obtained when required",
            "Special protections for children's data",
            "Limited data collection for educational purposes"
          ]
        }
      ]
    },
    {
      title: "Your Rights and Choices",
      content: [
        {
          subtitle: "Access and Control",
          items: [
            "Request access to your personal data",
            "Update or correct inaccurate information",
            "Delete your account and associated data",
            "Export your data in a portable format"
          ]
        },
        {
          subtitle: "Communication Preferences",
          items: [
            "Opt-out of marketing communications",
            "Choose notification frequency and types",
            "Control data sharing with third parties",
            "Set privacy preferences for your account"
          ]
        }
      ]
    },
    {
      title: "Data Retention",
      content: [
        {
          subtitle: "Retention Periods",
          items: [
            "Student exam data: 1 year after course completion",
            "Teacher account data: Until account deletion",
            "System logs: 90 days for security purposes",
            "Billing records: 7 years as required by law"
          ]
        },
        {
          subtitle: "Automatic Deletion",
          items: [
            "Inactive student accounts after 2 years",
            "Temporary exam data after completion",
            "Cached data cleared regularly",
            "Backup data purged according to schedule"
          ]
        }
      ]
    },
    {
      title: "International Data Transfers",
      content: [
        {
          subtitle: "Cross-Border Data Handling",
          items: [
            "Data processed primarily in the United States",
            "Adequate protections for international transfers",
            "Compliance with GDPR for European users",
            "Standard contractual clauses with vendors"
          ]
        }
      ]
    },
    {
      title: "Cookies and Tracking",
      content: [
        {
          subtitle: "Essential Cookies",
          items: [
            "Authentication and session management",
            "Security and fraud prevention",
            "User preferences and settings",
            "System functionality and performance"
          ]
        },
        {
          subtitle: "Analytics Cookies",
          items: [
            "Aggregate usage statistics (anonymized)",
            "Performance monitoring and optimization",
            "Error tracking and debugging",
            "Feature usage and improvement insights"
          ]
        }
      ]
    },
    {
      title: "Children's Privacy",
      content: [
        {
          subtitle: "Under 13 Years Old",
          items: [
            "No accounts created for children under 13",
            "All student accounts managed by teachers",
            "Minimal data collection for educational purposes",
            "Parental rights respected and protected"
          ]
        },
        {
          subtitle: "13-17 Years Old",
          items: [
            "Additional protections beyond adult users",
            "School supervision and oversight",
            "Limited data sharing capabilities",
            "Enhanced security monitoring"
          ]
        }
      ]
    }
  ];

  const lastUpdated = "September 1, 2024";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">🔒 Privacy Policy</h1>
            <p className="text-xl text-blue-100 mb-6 leading-relaxed">
              Your privacy is fundamental to everything we do. This policy explains 
              how we collect, use, and protect your information at ExamLock.
            </p>
            <div className="bg-white bg-opacity-20 rounded-lg px-6 py-3 inline-block">
              <span className="font-bold">Last Updated: {lastUpdated}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Summary */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-green-50 border border-green-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-green-800 mb-6 flex items-center">
                <span className="mr-3">✅</span>
                Privacy at a Glance
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-bold text-green-800 mb-3">What We Do:</h3>
                  <ul className="space-y-2 text-green-700">
                    <li>• Collect minimal data for educational purposes</li>
                    <li>• Use bank-level security to protect your information</li>
                    <li>• Give you control over your data</li>
                    <li>• Comply with FERPA, COPPA, and GDPR</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-bold text-green-800 mb-3">What We Don't Do:</h3>
                  <ul className="space-y-2 text-green-700">
                    <li>• Sell your data to third parties</li>
                    <li>• Use student data for advertising</li>
                    <li>• Collect unnecessary personal information</li>
                    <li>• Share data without proper consent</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {sections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-8 border-b border-gray-200 pb-4">
                  {sectionIndex + 1}. {section.title}
                </h2>
                
                <div className="space-y-8">
                  {section.content.map((subsection, subIndex) => (
                    <div key={subIndex} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
                      <h3 className="text-xl font-bold text-gray-800 mb-4">
                        {subsection.subtitle}
                      </h3>
                      <ul className="space-y-3">
                        {subsection.items.map((item, itemIndex) => (
                          <li key={itemIndex} className="flex items-start text-gray-700">
                            <span className="text-blue-500 mr-3 mt-1">•</span>
                            <span className="leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Contact Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-8 mb-16">
              <h2 className="text-2xl font-bold text-blue-800 mb-6 flex items-center">
                <span className="mr-3">📧</span>
                Questions About This Policy?
              </h2>
              <div className="text-blue-700 space-y-4">
                <p>
                  If you have questions about this Privacy Policy or our data practices, 
                  please contact our Privacy Team:
                </p>
                <div className="bg-white rounded-lg p-4 space-y-2">
                  <div><strong>Email:</strong> privacy@examlock.ai</div>
                  <div><strong>Address:</strong> ExamLock Privacy Team, [Address]</div>
                  <div><strong>Phone:</strong> +1 (555) 123-EXAM (for priority concerns)</div>
                </div>
                <p className="text-sm">
                  We typically respond to privacy inquiries within 48 hours during business days.
                </p>
              </div>
            </div>

            {/* Compliance Badges */}
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">🛡️ Privacy Certifications</h3>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="bg-white px-6 py-3 rounded-lg shadow border border-gray-200">
                  <div className="font-bold text-green-600">🇪🇺 GDPR Compliant</div>
                </div>
                <div className="bg-white px-6 py-3 rounded-lg shadow border border-gray-200">
                  <div className="font-bold text-blue-600">🇺🇸 COPPA Certified</div>
                </div>
                <div className="bg-white px-6 py-3 rounded-lg shadow border border-gray-200">
                  <div className="font-bold text-purple-600">🏫 FERPA Compliant</div>
                </div>
                <div className="bg-white px-6 py-3 rounded-lg shadow border border-gray-200">
                  <div className="font-bold text-orange-600">✅ SOC 2 Audited</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}